import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { withRouter } from "react-router-dom";
import { QueryContext } from "../../context/query";
import { continueState } from "./phoneNumber";
import { useVerifyCode, useResendCode } from '../../../Common/hooks';
import { Link, TextField } from "@mui/material";
import { globalConfig } from "../../../Common/configuration/config";
import FormDirections from "../../../Common/components/templates/formDirections";
import PrimaryButton from "../../../Common/components/templates/buttons/primary";
import SkipButton from "../../../Common/components/templates/buttons/skip";
import { useIntl, FormattedMessage } from 'react-intl';
import { getTranslationForCodeWithReplacements } from "../../../Common/localization/localizationHelper";
import SecondaryButton from "../../../Common/components/templates/buttons/secondary";

const VerificationCode = () => {
    const [verifyCodeClick, setVerifyCodeClick] = useState(false);
    const [resendCodeClick, setResendCodeClick] = useState(false);
    const formRef = useRef<HTMLFormElement>(null);
    const hasStartedRedirect = useRef(false);
    const { state } = useContext(QueryContext);
    const { verifyCode, handleVerifyCode } = useVerifyCode();
    const { resendCode, handleResendCode } = useResendCode();
    const config = globalConfig.get();
    const resendCodeCooldownSeconds = config.ResendCodeCooldownSeconds;
    const [resendCodeCooldown, setResendCodeCooldown] = useState(resendCodeCooldownSeconds);
    const [code, setCode] = useState("");
    const intl = useIntl();

    const changeCode = (event: any) => {
        setCode(event.target.value);
    };

    useEffect(() => {
        if (resendCodeCooldown === 0) {
            return;
        }

        const timer = window.setTimeout(() => {
            setResendCodeCooldown((currentCooldown) => Math.max(currentCooldown - 1, 0));
        }, 1000);

        return () => window.clearTimeout(timer);
    }, [resendCodeCooldown]);

    const verifyCodeMethod: any = useCallback(
        (token: string, code: string, phone: string) =>
            handleVerifyCode(token, code, phone),
        [handleVerifyCode]
    );
    const verifyCodeSubmit = (e: any) => {
        setVerifyCodeClick(true);
        setResendCodeClick(false);
        e.preventDefault();
        formRef.current?.checkValidity();
        if (formRef.current?.checkValidity() === true) {
            verifyCodeMethod(state.token, code, state.phoneNumber);
        }
    };

    const resendCodeMethod: any = useCallback(
        (token: string, phone: string) => handleResendCode(token, phone),
        [handleResendCode]
    );

    const resendCodeSubmit = () => {
        if (resendCodeCooldown > 0) {
            return;
        }

        setVerifyCodeClick(false);
        setResendCodeClick(true);
        setResendCodeCooldown(resendCodeCooldownSeconds);
        resendCodeMethod(state.token, state.phoneNumber);
    };

    useEffect(() => {
        if (verifyCode.result.isValid && state.auth0state && !hasStartedRedirect.current) {
            hasStartedRedirect.current = true;
            continueState(state.auth0state, config.Domain);
        }
    }, [verifyCode.result.isValid, state.auth0state, config.Domain]);

    const isButtonDisabled = (verifyCode.result.errorCode === "max_login_attempts_exceeded" || verifyCode.result.errorCode === "phone_number_already_linked") ||
                           (resendCode.result.errorCode === "max_login_attempts_exceeded" || resendCode.result.errorCode === "phone_number_already_linked");
    const isResendCodeDisabled = isButtonDisabled || resendCodeCooldown > 0;

    return (
        <form
            className="form"
            ref={formRef}
            onSubmit={(e) => verifyCodeSubmit(e)}>
            <FormDirections>
                <FormattedMessage id="passwordless.verificationCode.header" />
            </FormDirections>
            {
                verifyCodeClick && verifyCode.result.message && verifyCode.result.errorCode ? (
                    <div className="error">
                        {
                            getTranslationForCodeWithReplacements(verifyCode.result.errorCode, intl, {
                                "number": state.phoneNumber,
                                "email": state.email,
                                "code": code

                            })}
                    </div>
                ) : null}
            {
                resendCodeClick && resendCode.result.message ? (
                    <div className="error">
                        {
                            getTranslationForCodeWithReplacements(resendCode.result.errorCode, intl, {
                                "number": state.phoneNumber,
                                "email": state.email,
                                "code": code

                            })}
                    </div>
                ) : null}
            <div className="content">
                <label htmlFor="verifycode" className="label">
                    <FormattedMessage id="passwordless.verificationCode.label" />
                </label>
                <TextField
                    style={{
                        width: "100%",
                    }}
                    id="verifycode"
                    value={code}
                    required
                    variant="outlined"
                    inputProps={{
                        maxLength: 6,
                        'aria-label': intl?.messages["passwordless.verificationCode.label"]?.toString() || "Verification Code"
                    }}
                    onChange={(e) => {
                        const target = e.target as HTMLInputElement;
                        const translation = intl?.messages["invalidInputMessage"]?.toString();
                        target.setCustomValidity("");
                        changeCode(e)
                    }}
                    onInvalid={(e) => {
                        const target = e.target as HTMLInputElement;
                        const translation = intl?.messages["invalidInputMessage"]?.toString();
                        target.setCustomValidity(translation);
                    }}

                />
                <div className="buttons">
                    <PrimaryButton type="submit" variant="contained" color="primary" disabled={isButtonDisabled}>
                        <FormattedMessage id="confirm"></FormattedMessage>
                    </PrimaryButton>
                    <SecondaryButton
                        onClick={resendCodeSubmit}
                        disabled={isResendCodeDisabled}
                    >
                        <FormattedMessage id="passwordless.verificationCode.button.resend"></FormattedMessage>
                        {resendCodeCooldown > 0 ? (
                            <span aria-live="polite" style={{ marginLeft: "4px" }}>
                                ({resendCodeCooldown}s)
                            </span>
                        ) : null}
                    </SecondaryButton>
                    <SkipButton formNoValidate
                        color="secondary"
                        onClick={(e: any) => {
                            e.stopPropagation();
                            if (state.auth0state) continueState(state.auth0state, config.Domain);
                        }}
                    >
                        <FormattedMessage id="skipStep"></FormattedMessage>
                    </SkipButton>
                </div>
            </div>
        </form>
    );
};

export default withRouter(VerificationCode);
